/*
 * cc-tunnel
 * Tunnel Click & Collect : fiche produit (cc-quickview) et panneau « Votre retrait » (cc-pickup).
 *
 * Tout ce qui s'affiche vient du Liquid : la fiche est clonée depuis un <template> par
 * produit, le panneau est repeint par le rendu de section après chaque ajout. Le JS ne
 * calcule que le total du bouton d'ajout (shop.money_format) et l'état du créneau.
 * Les jours et les plages sont calculés en Liquid, dans le fuseau de la boutique.
 */

if (!customElements.get('cc-quickview')) {
  customElements.define(
    'cc-quickview',
    class CcQuickview extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized) return;
        this.dataset.initialized = 'true';

        this.tunnel = this.closest('.cc-tunnel');
        this.dialog = this.querySelector('[role="dialog"]');
        this.body = this.querySelector('[data-cc-body]');
        this.reassurance = this.querySelector('[data-cc-reassurance]');
        this.opener = null;

        this.tunnel.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-cc-open]');
          if (!trigger || trigger.disabled) return;
          this.open(trigger);
        });

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('input', this.onQuantity.bind(this));
        this.addEventListener('change', this.onQuantity.bind(this));
        this.onKeydown = this.onKeydown.bind(this);
      }

      // Format monétaire de la boutique (« {{amount_with_comma_separator}}€ » donne « 20,00€ »).
      static formatMoney(cents, format) {
        const pattern = /\{\{\s*(\w+)\s*\}\}/;
        const match = format.match(pattern);
        const amount = (precision, thousands, decimal) => {
          const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
          const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return fraction ? `${grouped}${decimal}${fraction}` : grouped;
        };
        const formats = {
          amount: () => amount(2, ',', '.'),
          amount_no_decimals: () => amount(0, ',', '.'),
          amount_with_comma_separator: () => amount(2, '.', ','),
          amount_no_decimals_with_comma_separator: () => amount(0, '.', ','),
          amount_with_apostrophe_separator: () => amount(2, "'", '.'),
          amount_no_decimals_with_space_separator: () => amount(0, ' ', ''),
          amount_with_space_separator: () => amount(2, ' ', ','),
          amount_with_period_and_space_separator: () => amount(2, ' ', '.'),
        };
        if (!match) return amount(2, ',', '.');
        return format.replace(pattern, (formats[match[1]] || formats.amount)());
      }

      onClick(event) {
        // Un clic sur le voile ferme la fiche.
        if (event.target === this) return this.close();
        if (event.target.closest('[data-cc-close]')) return this.close();
        if (event.target.closest('[data-cc-add]')) return this.add();
      }

      onKeydown(event) {
        if (event.key === 'Escape') this.close();
      }

      onQuantity(event) {
        if (!event.target.matches('[data-cc-qty]')) return;
        const feedback = this.body.querySelector('[data-cc-feedback]');
        if (feedback) feedback.hidden = true;
        this.updateButton();
      }

      /* ---------- ouverture ---------- */

      open(trigger) {
        const template = trigger.closest('[data-cc-card]').querySelector('[data-cc-detail]');
        if (!template) return;

        this.opener = trigger;
        this.body.replaceChildren(template.content.cloneNode(true));
        const content = this.body.querySelector('.cc-detail__content');
        if (content && this.reassurance) content.appendChild(this.reassurance.content.cloneNode(true));
        this.updateButton();

        this.hidden = false;
        this.body.scrollTop = 0;
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this.onKeydown);
        trapFocus(this.dialog, this.querySelector('[data-cc-close]'));
      }

      close() {
        if (this.hidden) return;
        this.hidden = true;
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', this.onKeydown);
        // Rendre le focus à la carte d'où l'on vient.
        removeTrapFocus(this.opener);
      }

      /* ---------- quantités et bouton ---------- */

      items() {
        return [...this.body.querySelectorAll('[data-cc-qty]')]
          .map((input) => ({
            id: Number(input.dataset.variantId),
            quantity: Math.max(0, parseInt(input.value, 10) || 0),
            price: Number(input.dataset.price) || 0,
          }))
          .filter((item) => item.quantity > 0);
      }

      updateButton() {
        const button = this.body.querySelector('[data-cc-add]');
        if (!button) return;

        const items = this.items();
        const count = items.reduce((sum, item) => sum + item.quantity, 0);
        const total = items.reduce((sum, item) => sum + item.quantity * item.price, 0);
        const unit = count > 1 ? this.dataset.itemsPlural : this.dataset.itemsSingular;

        button.textContent = count
          ? `${this.dataset.addLabel} ${count} ${unit} · ${CcQuickview.formatMoney(total, this.dataset.moneyFormat)}`
          : this.dataset.chooseLabel;
        button.classList.toggle('is-muted', !count);
      }

      feedback(message, isError) {
        const target = this.body.querySelector('[data-cc-feedback]');
        if (!target) return;
        target.textContent = message;
        target.classList.toggle('is-error', Boolean(isError));
        target.hidden = false;
      }

      /* ---------- panier ---------- */

      add() {
        const button = this.body.querySelector('[data-cc-add]');
        if (button.getAttribute('aria-disabled') === 'true') return;

        const items = this.items().map(({ id, quantity }) => ({ id, quantity }));
        if (!items.length) return this.feedback(this.dataset.emptyMessage, true);

        const sectionId = this.tunnel.dataset.sectionId;
        const pickup = this.tunnel.querySelector('cc-pickup');
        const cart = document.querySelector('cart-drawer');
        const cartSections = cart ? cart.getSectionsToRender().map((section) => section.id) : ['cart-icon-bubble'];
        button.setAttribute('aria-disabled', 'true');

        // Le choix affiché (Maison et créneau) part avant l'article, pour que le tiroir l'affiche
        // dès son ouverture. Un échec ici n'empêche pas l'ajout : le panneau réessaiera.
        const choice = pickup && pickup.differs() ? pickup.save({ silent: true }).catch(() => {}) : Promise.resolve();

        choice
          .then(() =>
            fetch(routes.cart_add_url, {
              ...fetchConfig(),
              body: JSON.stringify({
                items,
                sections: [sectionId, ...cartSections],
                sections_url: window.location.pathname,
              }),
            })
          )
          .then((response) => response.json())
          .then((data) => {
            if (data.status) throw new Error(data.description || data.message);

            // La fiche se ferme, sans ouvrir le tiroir (Quentin, 23/10) : le panneau, et la barre
            // basse sur mobile, montrent l'ajout.
            this.close();
            this.refresh(data.sections || {});
            if (cart) {
              // Le tiroir est mis à jour pour l'icône du panier : rendu de renderContents (Dawn),
              // sans open(). Sans « is-empty » : chargé vide, il chercherait son état vide.
              cart.classList.remove('is-empty');
              cart.getSectionsToRender().forEach((section) => {
                const target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
                if (target && data.sections[section.id]) {
                  target.innerHTML = cart.getSectionInnerHTML(data.sections[section.id], section.selector);
                }
              });
              // Le voile est remplacé avec le contenu : il faut lui rendre sa fermeture au clic.
              const overlay = cart.querySelector('#CartDrawer-Overlay');
              if (overlay) overlay.addEventListener('click', cart.close.bind(cart));
            }
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cc-tunnel', cartData: data });
          })
          .catch(() => this.feedback(this.dataset.errorMessage, true))
          .finally(() => button.removeAttribute('aria-disabled'));
      }

      // Le panneau et la pastille du header sont repeints depuis le serveur plutôt que
      // recalculés ici.
      refresh(sections) {
        const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble && sections['cart-icon-bubble']) {
          const fresh = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
          if (fresh) bubble.innerHTML = fresh.innerHTML;
        }

        const pickup = this.tunnel.querySelector('cc-pickup');
        if (!pickup) return;

        const sectionId = this.tunnel.dataset.sectionId;
        if (sections[sectionId]) return pickup.refresh(parse(sections[sectionId]));

        fetch(`${window.location.pathname}?section_id=${sectionId}`)
          .then((response) => response.text())
          .then((text) => pickup.refresh(parse(text)))
          .catch(() => {});
      }
    }
  );
}

if (!customElements.get('cc-pickup')) {
  customElements.define(
    'cc-pickup',
    class CcPickup extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized) return;
        this.dataset.initialized = 'true';

        this.tunnel = this.closest('.cc-tunnel');
        this.panel = this.closest('[data-cc-panel]');
        this.bar = this.tunnel.querySelector('[data-cc-bar]');
        this.picker = this.querySelector('.cc-slot__picker');
        this.toggle = this.querySelector('[data-cc-slot-toggle]');
        this.request = null;
        this.saveTimer = null;
        this.paying = false;
        this.panelBelow = false;

        // Maisons de retrait par variante, demandées à Shopify (loadRetirable) : la page ne les
        // fournit que pour la première variante de chaque produit.
        this.retirable = {};
        this.loading = new Set();

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));
        if (this.bar) {
          this.bar.addEventListener('click', (event) => {
            if (event.target.closest('[data-cc-pay]')) this.pay();
          });
        }

        // Retour arrière depuis le paiement (cache de page) : le bouton ne doit pas rester bloqué.
        this.onPageShow = (event) => {
          if (!event.persisted || !this.paying) return;
          this.paying = false;
          this.update();
        };
        window.addEventListener('pageshow', this.onPageShow);

        this.syncTimes();
        this.update();
        this.observe();
        this.replaceStale();
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        window.removeEventListener('pageshow', this.onPageShow);
        clearTimeout(this.saveTimer);
      }

      /* ---------- état ---------- */

      selected(selector) {
        return this.querySelector(`${selector}[aria-checked="true"]`);
      }

      get count() {
        return Number(this.querySelector('[data-cc-summary]').dataset.count) || 0;
      }

      hasSlot() {
        return Boolean(this.selected('[data-cc-day]') && this.selected('[data-cc-time]'));
      }

      // Les quatre clés partent toujours ensemble. Sans plage choisie, date, créneau et
      // référence sont vidés : la commande ne doit pas partir avec un choix abandonné.
      attributes() {
        const day = this.selected('[data-cc-day]');
        const time = this.selected('[data-cc-time]');
        const store = this.selected('[data-cc-store]');
        const complete = Boolean(day && time);

        return {
          'Retrait - date': complete ? day.dataset.label : '',
          'Retrait - créneau': complete ? time.dataset.label : '',
          'Retrait - référence': complete ? `${day.dataset.ccDay}|${time.dataset.ccTime}` : '',
          'Retrait - Maison': store ? store.dataset.name : '',
        };
      }

      /* ---------- interactions ---------- */

      onClick(event) {
        if (this.paying) return;
        const target = event.target.closest('button');
        if (!target || target.disabled) return;

        if (target.matches('[data-cc-store]')) return this.selectStore(target);
        if (target.matches('[data-cc-slot-toggle]')) return this.togglePicker();
        if (target.matches('[data-cc-day]')) return this.selectDay(target);
        if (target.matches('[data-cc-time]')) return this.selectTime(target);
        if (target.matches('[data-cc-remove]')) return this.changeLine(target.closest('[data-cc-line]'), 0);
        if (target.matches('[data-cc-qty]')) return this.changeLine(target.closest('[data-cc-line]'), Number(target.dataset.ccQty));
        if (target.matches('[data-cc-pay]')) return this.pay();
      }

      // Groupes de boutons radio : les flèches déplacent le choix (motif ARIA radiogroup).
      onKeydown(event) {
        const radio = event.target.closest('[role="radio"]');
        const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
        if (!radio || !step) return;

        event.preventDefault();
        const radios = [...radio.closest('[role="radiogroup"]').querySelectorAll('[role="radio"]:not([disabled])')];
        const next = radios[(radios.indexOf(radio) + step + radios.length) % radios.length];
        if (!next) return;
        next.focus();
        next.click();
      }

      check(radio) {
        radio
          .closest('[role="radiogroup"]')
          .querySelectorAll('[role="radio"]')
          .forEach((item) => {
            item.setAttribute('aria-checked', String(item === radio));
            item.tabIndex = item === radio ? 0 : -1;
          });
      }

      selectStore(tab) {
        if (tab.getAttribute('aria-checked') === 'true') return;
        this.check(tab);
        this.querySelectorAll('[data-cc-store-card]').forEach((card) => {
          card.hidden = card.dataset.ccStoreCard !== tab.dataset.ccStore;
        });
        this.changed();
      }

      togglePicker() {
        const open = this.toggle.getAttribute('aria-expanded') !== 'true';
        this.toggle.setAttribute('aria-expanded', String(open));
        this.toggle.textContent = open ? this.dataset.closeLabel : this.dataset.changeLabel;
        this.picker.hidden = !open;
      }

      // Un autre jour vide la plage, comme la maquette : la cliente la choisit à nouveau.
      selectDay(day) {
        if (day.getAttribute('aria-checked') === 'true') return;
        this.check(day);

        const times = [...this.querySelectorAll('[data-cc-time]')];
        times.forEach((time, index) => {
          time.setAttribute('aria-checked', 'false');
          time.tabIndex = index === 0 ? 0 : -1;
        });

        this.syncTimes();
        this.querySelector('[data-cc-slot-day]').textContent = day.dataset.label;
        this.querySelector('[data-cc-slot-time]').textContent = this.dataset.noSlotLabel;
        this.changed();
      }

      // Le dimanche, chaque plage finit à l'heure de fermeture du dimanche (réglage de la section).
      syncTimes() {
        const day = this.selected('[data-cc-day]');
        const sunday = Boolean(day) && day.dataset.dow === '0';
        this.querySelectorAll('[data-cc-time]').forEach((time) => {
          time.dataset.label = sunday ? time.dataset.labelSunday : time.dataset.labelDefault;
          time.textContent = time.dataset.label;
        });

        const chosen = this.selected('[data-cc-time]');
        if (chosen) this.querySelector('[data-cc-slot-time]').textContent = chosen.dataset.label;
      }

      selectTime(time) {
        if (time.getAttribute('aria-checked') === 'true') return;
        this.check(time);
        this.querySelector('[data-cc-slot-time]').textContent = time.dataset.label;
        this.changed();
      }

      changed() {
        this.showMessage('');
        this.update();
        this.scheduleSave();
      }

      /* ---------- affichage ---------- */

      update() {
        const count = this.count;
        const slot = this.hasSlot();
        const blocked = this.markUnavailable();
        const total = this.querySelector('[data-cc-summary]').dataset.total;

        let label = this.dataset.payLabel;
        if (this.paying) label = this.dataset.payingLabel;
        else if (count > 0) label = slot ? `${this.dataset.payLabel} ${total}` : this.dataset.noSlotLabel;

        const cta = this.querySelector('[data-cc-pay]');
        cta.textContent = label;
        cta.disabled = count === 0 || blocked;
        cta.classList.toggle('is-muted', count > 0 && !slot);

        if (this.bar) {
          const barCta = this.bar.querySelector('[data-cc-pay]');
          barCta.textContent = label;
          barCta.disabled = blocked;
        }

        // Le message d'article indisponible reste tant que l'article est dans le panier.
        if (blocked) this.showMessage(this.dataset.unavailableMessage);
        else if (this.unavailableShown) this.showMessage('');
        this.unavailableShown = blocked;
        [cta, this.bar && this.bar.querySelector('[data-cc-pay]')].forEach((button) => {
          if (!button) return;
          if (this.paying) button.setAttribute('aria-disabled', 'true');
          else button.removeAttribute('aria-disabled');
        });

        this.updateBar();
      }

      updateBar() {
        if (!this.bar) return;
        this.bar.hidden = !(this.count > 0 && this.panelBelow);
      }

      showMessage(message) {
        [this.querySelector('[data-cc-message]'), this.bar && this.bar.querySelector('[data-cc-bar-message]')].forEach(
          (target) => {
            if (!target) return;
            target.textContent = message;
            target.hidden = !message;
          }
        );
      }

      // Barre basse : seulement quand le panneau est sous l'écran (au moins 80 px visibles
      // suffisent à la masquer, comme la maquette). Au-dessus de l'écran, la section se
      // termine : la barre réapparaîtrait en plein texte.
      observe() {
        if (!this.bar || !('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[entries.length - 1];
            this.panelBelow = !entry.isIntersecting && entry.boundingClientRect.top > 0;
            this.updateBar();
          },
          { rootMargin: '-80px 0px -80px 0px' }
        );
        this.observer.observe(this.panel);
      }

      // Après un ajout : seuls le compteur, les lignes et le total viennent du serveur.
      // Le reste du panneau garde l'état choisi (Maison, créneau, choix ouvert).
      refresh(doc) {
        const swap = (selector, scope) => {
          const fresh = doc.querySelector(selector);
          const current = scope && scope.querySelector(selector);
          if (fresh && current) current.replaceWith(fresh);
        };
        swap('[data-cc-count]', this);
        swap('[data-cc-summary]', this);
        swap('[data-cc-bar-count]', this.bar);
        swap('[data-cc-bar-total]', this.bar);
        this.update();
        // Dès le premier ajout, le choix affiché (créneau présélectionné compris) part avec la
        // commande, même si la cliente paie ensuite depuis le panier sans passer par « Payer ».
        if (this.count > 0 && this.differs()) this.scheduleSave();
      }

      /* ---------- attributs du panier ---------- */

      scheduleSave() {
        clearTimeout(this.saveTimer);
        this.saveTimer = setTimeout(() => {
          this.save().catch((error) => {
            if (error.name !== 'AbortError') this.showMessage(this.dataset.errorMessage);
          });
        }, ON_CHANGE_DEBOUNCE_TIMER);
      }

      // silent : pas d'événement cartUpdate, l'appelant en publie un juste après (ajout).
      save({ silent = false } = {}) {
        const attributes = this.attributes();
        if (this.request) this.request.abort();
        this.request = new AbortController();

        return fetch(routes.cart_update_url, {
          ...fetchConfig(),
          body: JSON.stringify({ attributes }),
          signal: this.request.signal,
        })
          .then((response) => {
            if (!response.ok) throw new Error(String(response.status));
            return response.json();
          })
          .then((cartData) => {
            this.dataset.cartRef = attributes['Retrait - référence'];
            this.dataset.cartDate = attributes['Retrait - date'];
            this.dataset.cartSlot = attributes['Retrait - créneau'];
            this.dataset.cartStore = attributes['Retrait - Maison'];
            if (!silent) publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cc-tunnel', cartData });
            return cartData;
          });
      }

      // Lignes qu'on ne peut pas retirer dans la Maison choisie : barrées, avec l'encart
      // « Indisponible à … » et « Retirer ». Renvoie vrai s'il en reste au moins une.
      markUnavailable() {
        const store = this.selected('[data-cc-store]');
        const name = store ? store.dataset.name : '';
        let blocked = false;

        this.querySelectorAll('[data-cc-line]').forEach((line) => {
          const known = this.retirable[line.dataset.variantId];
          if (known === undefined) this.loadRetirable(line.dataset.variantId);
          // Tant que Shopify n'a pas répondu, la ligne n'est pas bloquée.
          const out = Boolean(name) && known !== undefined && !known.includes(`|${name}|`);
          line.classList.toggle('is-unavailable', out);
          const alert = line.querySelector('[data-cc-line-alert]');
          if (alert) {
            alert.hidden = !out;
            if (out) alert.querySelector('[data-cc-line-store]').textContent = name;
          }
          if (out) blocked = true;
        });

        return blocked;
      }

      // Une requête par variante, gardée en mémoire : /variants/<id>/ rend la section avec cette
      // variante choisie, seul contexte où Shopify donne ses lieux de retrait.
      loadRetirable(id) {
        if (!id || this.loading.has(id)) return;
        this.loading.add(id);
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        fetch(`${root}variants/${id}/?section_id=cc-retrait-variante`)
          .then((response) => response.text())
          .then((text) => {
            const data = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-cc-retirable]');
            if (!data) return;
            this.retirable[id] = data.dataset.ccRetirable;
            this.update();
          })
          .catch(() => {})
          .finally(() => this.loading.delete(id));
      }

      // Quantité changée (0 = « Retirer »), puis le panneau est repeint depuis le serveur.
      changeLine(line, quantity) {
        if (!line || line.dataset.removing) return;
        line.dataset.removing = 'true';

        const sectionId = this.tunnel.dataset.sectionId;
        const quickview = this.tunnel.querySelector('cc-quickview');

        fetch(routes.cart_change_url, {
          ...fetchConfig(),
          body: JSON.stringify({
            id: line.dataset.key,
            quantity,
            sections: [sectionId, 'cart-icon-bubble'],
            sections_url: window.location.pathname,
          }),
        })
          .then((response) => response.json())
          .then((data) => {
            if (data.status) throw new Error(data.description || data.message);
            if (quickview) quickview.refresh(data.sections || {});
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cc-tunnel', cartData: data });
          })
          .catch(() => {
            delete line.dataset.removing;
            this.showMessage(this.dataset.errorMessage);
          });
      }

      // Le choix affiché diffère-t-il de celui enregistré dans le panier ?
      differs() {
        const current = this.attributes();
        return (
          this.dataset.cartRef !== current['Retrait - référence'] ||
          this.dataset.cartDate !== current['Retrait - date'] ||
          this.dataset.cartSlot !== current['Retrait - créneau'] ||
          this.dataset.cartStore !== current['Retrait - Maison']
        );
      }

      // Une référence restée dans le panier qui ne correspond plus au choix affiché
      // (date passée, plage ou Maison retirée) partirait avec la commande : on la remplace.
      replaceStale() {
        if (this.dataset.cartRef && this.differs()) this.scheduleSave();
      }

      // « Payer » écrit toujours les attributs juste avant de partir au paiement.
      pay() {
        if (this.paying || this.count === 0) return;
        if (!this.hasSlot()) return this.showMessage(this.dataset.slotMessage);

        clearTimeout(this.saveTimer);
        this.paying = true;
        this.showMessage('');
        this.update();

        this.save()
          .then(() => {
            window.location.href = '/checkout';
          })
          .catch(() => {
            this.paying = false;
            this.update();
            this.showMessage(this.dataset.errorMessage);
          });
      }
    }
  );
}
